import type { ChangeEvent } from "react";
import type {
  CatalogBrandDto,
  CatalogCategoryDto,
  CatalogPricelistDto
} from "../../api/schemas";
import {
  pricelistSelectionKey,
  quickPricelistDefinitions,
  resolveQuickPricelist
} from "../../domain/catalog";

interface CatalogPanelProps {
  brands: CatalogBrandDto[];
  selectedBrand: CatalogBrandDto | undefined;
  selectedCategory: CatalogCategoryDto | undefined;
  selectedPricelist: CatalogPricelistDto | undefined;
  onBrandChange: (brand: string) => void;
  onCategoryChange: (category: string) => void;
  onPricelistChange: (key: string) => void;
  onQuickPricelistChange: (brand: string, category: string, key: string) => void;
}

export function CatalogPanel({
  brands,
  selectedBrand,
  selectedCategory,
  selectedPricelist,
  onBrandChange,
  onCategoryChange,
  onPricelistChange,
  onQuickPricelistChange
}: CatalogPanelProps) {
  function handleBrandChange(event: ChangeEvent<HTMLSelectElement>) {
    onBrandChange(event.currentTarget.value);
  }

  function handleCategoryChange(event: ChangeEvent<HTMLSelectElement>) {
    onCategoryChange(event.currentTarget.value);
  }

  function handlePricelistChange(event: ChangeEvent<HTMLSelectElement>) {
    onPricelistChange(event.currentTarget.value);
  }

  const quickPricelists = quickPricelistDefinitions.map((definition) => ({
    definition,
    resolved: resolveQuickPricelist(brands, definition)
  }));

  return (
    <section className="toolbar-band" aria-label="Pricelist filters">
      <div className="quick-pricelists" role="group" aria-label="Common KEI Pricelists">
        <span className="quick-pricelists-label">Common KEI Pricelists</span>
        {quickPricelists.map(({ definition, resolved }) => {
          const isActive =
            resolved !== undefined &&
            selectedBrand?.brand === resolved.brand &&
            selectedCategory?.category === resolved.category &&
            selectedPricelist !== undefined &&
            pricelistSelectionKey(selectedPricelist) === resolved.key;

          return (
            <button
              key={definition.id}
              type="button"
              className="quick-pricelist-button"
              aria-pressed={isActive}
              disabled={!resolved}
              title={
                resolved?.pricelist.display_name ?? `${definition.label} is currently unavailable`
              }
              onClick={() => {
                if (resolved) {
                  onQuickPricelistChange(resolved.brand, resolved.category, resolved.key);
                }
              }}
            >
              {definition.label}
            </button>
          );
        })}
      </div>

      <label className="field" htmlFor="brand-select">
        <span>Brand</span>
        <select
          id="brand-select"
          value={selectedBrand?.brand ?? ""}
          onChange={handleBrandChange}
        >
          {brands.map((brand) => (
            <option key={brand.brand} value={brand.brand}>
              {brand.brand.toUpperCase()}
            </option>
          ))}
        </select>
      </label>

      <label className="field" htmlFor="category-select">
        <span>Category</span>
        <select
          id="category-select"
          value={selectedCategory?.category ?? ""}
          onChange={handleCategoryChange}
          disabled={!selectedBrand}
        >
          {selectedBrand?.categories.map((category) => (
            <option key={category.category} value={category.category}>
              {category.category}
            </option>
          ))}
        </select>
      </label>

      <label className="field field-wide" htmlFor="pricelist-select">
        <span>Pricelist</span>
        <select
          id="pricelist-select"
          value={selectedPricelist ? pricelistSelectionKey(selectedPricelist) : ""}
          onChange={handlePricelistChange}
          disabled={!selectedCategory}
        >
          {selectedCategory?.pricelists.map((pricelist) => (
            <option
              key={pricelistSelectionKey(pricelist)}
              value={pricelistSelectionKey(pricelist)}
            >
              {pricelist.display_name}
            </option>
          ))}
        </select>
      </label>
    </section>
  );
}
